import {
  EditOutlined,
  FolderCopyOutlined,
  FolderDeleteOutlined,
  FolderSpecialOutlined,
  GroupsOutlined,
  LockOutlined,
  MoreHoriz,
  MoreVert,
  Share,
} from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useClassicNavigate } from '../../../hooks/useClassicNavigate';
import {
  deleteFolder,
  duplicateFolder,
  getFolder,
  getFolders,
  updateFolder,
} from '../../../redux/actions/folderActions';
import { updateRelation } from '../../../redux/actions/userSpecificRelationsActions';
import { myFolder } from '../../../redux/consts';
import { selectFolders } from '../../../redux/reducers/folderReducer';
import { AppDispatch } from '../../../redux/store';
import { IconLabel } from '../../../theme/CustomComponents';
import { Folder } from '../../../Types/folder';
import ConfirmationDialog from '../../DealGPT/PhaseTwo/ConfirmationDialog';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useAuth } from '../../Providers/AuthProvider';
import ShareDialog from '../ShareDialog';
import DeleteFolderOptionsDialog from './DeleteFolderOptionsDialog';
import MakePrivateDialog from './MakePrivateDialog';
import MoveToDialog from './MoveToDialog';

const FolderOptionsMenu = ({
  folder,
  horizontalOptions = false,
}: {
  folder: Folder;
  horizontalOptions?: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useClassicNavigate();
  const analytics = useAnalytics();
  const { currentFolder } = useSelector(selectFolders);
  const { currentUser, isAdmin } = useAuth();

  const [anchorFolderContextMenu, setAnchorFolderContextMenu] = useState<null | HTMLElement>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [makePrivateOpen, setMakePrivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const handlePinFolderToggle = useCallback(
    (newValue: boolean) => {
      analytics.event(
        AnalyticsEvent.WM_NIGEL_FOLDER_PINNED,
        {
          id: folder.id,
        },
        { folderId: folder.id, fileWasPinned: !newValue, fileNowPinned: newValue }
      );
      dispatch(updateRelation({ folderId: folder.id, pinned: newValue })).then(() => {
        dispatch(getFolders());
        if (currentFolder?.id === folder.id) {
          dispatch(getFolder({ folderId: folder.id, isAdmin }));
        }
        setAnchorFolderContextMenu(null);
      });
    },
    [folder, dispatch, currentFolder?.id, analytics, isAdmin]
  );

  const handleShareFolder = useCallback(
    (userIds: string[]) => {
      analytics.event(
        AnalyticsEvent.WM_NIGEL_FOLDER_SHARED,
        {
          id: folder.id,
        },
        { folderId: folder.id, contributors: userIds }
      );
      dispatch(
        updateFolder({ folderId: folder.id, updatedFields: { contributors: userIds } })
      ).then(() => {
        dispatch(getFolders());
        if (currentFolder?.id === folder.id) {
          dispatch(getFolder({ folderId: folder.id, isAdmin }));
        }
        setAnchorFolderContextMenu(null);
      });
    },
    [folder, dispatch, currentFolder?.id, analytics, isAdmin]
  );

  const handleMakeFolderPrivate = useCallback(() => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_FOLDER_SHARED,
      {
        id: folder.id,
      },
      { folderId: folder.id, contributors: [] }
    );
    dispatch(updateFolder({ folderId: folder.id, updatedFields: { contributors: [] } })).then(
      () => {
        dispatch(getFolders());
        if (currentFolder?.id === folder.id) {
          dispatch(getFolder({ folderId: folder.id, isAdmin }));
        }
        setAnchorFolderContextMenu(null);
      }
    );
  }, [currentFolder?.id, dispatch, folder.id, analytics, isAdmin]);

  const handleDeleteFolder = useCallback(
    (deleteConversations: boolean, newFolderId?: Folder['id']) => {
      analytics.event(
        AnalyticsEvent.WM_NIGEL_FOLDER_DELETE_CONFIRM,
        {
          id: folder.id,
        },
        { folderId: folder.id }
      );
      dispatch(deleteFolder({ folderId: folder.id, deleteConversations, newFolderId })).then(() => {
        dispatch(getFolders());

        if (newFolderId) {
          navigate(`/folder/${newFolderId}`);
        } else {
          navigate(`/folder/${myFolder.id}`);
        }
      });
    },
    [folder, dispatch, navigate, analytics]
  );

  const handleShouldDeleteFolder = (deleteConversations: boolean) => {
    setDeleteOpen(false);

    if (deleteConversations) {
      handleDeleteFolder(true);
      return;
    } else {
      setMoveOpen(true);
    }
  };

  const handleDuplicateFolder = useCallback(async () => {
    setDuplicateOpen(false);

    analytics.event(
      AnalyticsEvent.WM_NIGEL_FOLDER_DUPLICATE,
      { id: folder.id },
      { folderId: folder.id }
    );

    const duplicateFolderAction = await dispatch(duplicateFolder({ folderId: folder.id }));
    const newFolder = duplicateFolderAction.payload as Folder;

    if (newFolder.id) {
      await dispatch(getFolders());
      navigate(`/folder/${newFolder.id}/edit`);
    }
  }, [dispatch, navigate, folder, analytics]);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Tooltip
        id={`open-folder-card-settings-${folder.id}`}
        title={<Typography variant="caption"> Folder settings </Typography>}
        PopperProps={{ keepMounted: true }}
      >
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            setAnchorFolderContextMenu(event.currentTarget);
          }}
          aria-labelledby={`open-folder-card-settings-${folder.id}`}
          sx={{ p: 0 }}
        >
          {horizontalOptions ? (
            <MoreHoriz sx={{ color: '#070154' }} />
          ) : (
            <MoreVert sx={{ color: '#070154' }} />
          )}
        </IconButton>
      </Tooltip>
      <ShareDialog
        ownerId={folder.ownerId}
        currentUsers={folder.contributors}
        isOpen={shareOpen}
        setIsOpen={setShareOpen}
        onShare={handleShareFolder}
      />
      <MakePrivateDialog
        isOpen={makePrivateOpen}
        setIsOpen={setMakePrivateOpen}
        onMakePrivate={handleMakeFolderPrivate}
      />
      <DeleteFolderOptionsDialog
        isOpen={deleteOpen}
        setIsOpen={setDeleteOpen}
        onDelete={handleShouldDeleteFolder}
      />
      <MoveToDialog
        folderName={folder.name}
        folderId={folder.id}
        isOpen={moveOpen}
        setIsOpen={setMoveOpen}
        onMove={(folderId: Folder['id']) => {
          analytics.event(
            AnalyticsEvent.WM_NIGEL_FOLDER_MOVE_CONTENTS,
            {
              id: folder.id,
            },
            { oldFolderId: folder.id, newFolderId: folderId }
          );
          handleDeleteFolder(false, folderId);
          setMoveOpen(false);
        }}
      />
      {duplicateOpen && (
        <ConfirmationDialog
          title="Confirm Folder Duplication"
          close={() => setDuplicateOpen(false)}
          confirm={handleDuplicateFolder}
          message={
            <Typography variant="body1" gutterBottom>
              You are about to duplicate the folder <b>{folder.name}</b>. Please review the details
              below:
              <ul>
                <li>
                  Folder settings including folder knowledge will be copied to the new folder.
                </li>
                <li>Folder contributors will not be copied.</li>
                <li>Any prompts pinned to the folder will be added to the new folder as well.</li>
                <li>All files in the folder will be duplicated and moved to the new folder.</li>
                <li>You will become the new owner of the new folder.</li>
              </ul>
            </Typography>
          }
          confirmLabel="Confirm"
        />
      )}
      <Menu
        sx={{ mt: '16px' }}
        anchorEl={anchorFolderContextMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorFolderContextMenu)}
        onClose={() => setAnchorFolderContextMenu(null)}
      >
        <MenuItem
          onClick={() => {
            handlePinFolderToggle(!folder.pinned);
          }}
        >
          <IconLabel>
            <FolderSpecialOutlined
              sx={folder.pinned ? { color: '#50658E' } : { color: '#F900D3' }}
            />
            {folder.pinned ? 'Unpin Folder' : 'Pin Folder'}
          </IconLabel>
        </MenuItem>
        <MenuItem
          disabled={folder.ownerId !== currentUser?.id}
          onClick={() => {
            analytics.event(
              AnalyticsEvent.WM_NIGEL_FOLDER_EDIT,
              {
                id: folder.id,
              },
              { folderId: folder.id }
            );
            navigate(`/folder/${folder.id}/edit`);
          }}
        >
          <IconLabel>
            <EditOutlined />
            Edit Folder
          </IconLabel>
        </MenuItem>
        <MenuItem onClick={() => setShareOpen(true)}>
          <IconLabel>
            {folder.contributors.length > 0 ? (
              <>
                <GroupsOutlined />
                Manage Folder Members
              </>
            ) : (
              <>
                <Share />
                Share Folder With...
              </>
            )}
          </IconLabel>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorFolderContextMenu(null);
            setDuplicateOpen(true);
          }}
        >
          <IconLabel>
            <FolderCopyOutlined />
            Duplicate Folder
          </IconLabel>
        </MenuItem>
        {folder.contributors.length > 0 && (
          <MenuItem
            onClick={() => {
              setMakePrivateOpen(true);
            }}
            sx={{ borderTop: '1px solid #C4C4C4' }}
            disabled={folder.ownerId !== currentUser?.id}
          >
            <IconLabel>
              <LockOutlined sx={{ color: '#F52C00' }} />
              Make Folder Private
            </IconLabel>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            analytics.event(
              AnalyticsEvent.WM_NIGEL_FOLDER_DELETE,
              {
                id: folder.id,
              },
              { folderId: folder.id }
            );
            setDeleteOpen(true);
            setAnchorFolderContextMenu(null);
          }}
          sx={folder.contributors.length === 0 ? { borderTop: '1px solid #C4C4C4' } : {}}
          disabled={folder.ownerId !== currentUser?.id}
        >
          <IconLabel>
            <FolderDeleteOutlined sx={{ color: '#F52C00' }} />
            Delete Folder & Contents
          </IconLabel>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default FolderOptionsMenu;
